import React, { useState } from 'react';

/**
 * ModelCheatSheet — collapsible reference panel that shows cost / speed /
 * capability tradeoffs for each provider+model combo in AI nodes.
 *
 * Supports four categories:
 *   'llm'   — text LLMs (ai_agent, perplexity, etc.)
 *   'image' — image generation (DALL-E, Stability, Flux)
 *   'stt'   — speech-to-text (Whisper, Azure STT, Google STT)
 *   'tts'   — text-to-speech (OpenAI TTS, Azure, ElevenLabs)
 *
 * Props:
 *   category      — 'llm' | 'image' | 'stt' | 'tts' (default 'llm')
 *   providerField — field name for provider in formData (default 'provider')
 *   modelField    — field name for model in formData (default 'model')
 *   formData      — current node's form state
 */

// ─── LLM ───
const PROVIDERS_LLM = {
  openai: {
    label: 'OpenAI',
    models: [
      { id: 'gpt-5',             tier: 'premium',  speed: 'fast',     cost: '$0.625/$5 per 1M',   detail: '272K', note: 'Current flagship. Best general-purpose. Multimodal.' },
      { id: 'gpt-4.1',           tier: 'premium',  speed: 'medium',   cost: '$2/$8 per 1M',       detail: '1M',   note: 'Huge context. Best for long-document tasks.' },
      { id: 'gpt-4o',            tier: 'premium',  speed: 'fast',     cost: '$2.50/$10 per 1M',   detail: '128K', note: 'Older multimodal. Prefer gpt-5 for new work.' },
      { id: 'gpt-4o-mini',       tier: 'value',    speed: 'fast',     cost: '$0.15/$0.60 per 1M', detail: '128K', note: 'Cheap and reliable. Good default for high-volume tasks.' },
      { id: 'o3',                tier: 'reasoning', speed: 'slow',    cost: '$2/$8 per 1M',       detail: '200K', note: 'Chain-of-thought. Math / code / logic. Replaced o1.' },
      { id: 'o4-mini',           tier: 'reasoning', speed: 'medium',  cost: '$1.10/$4.40 per 1M', detail: '200K', note: 'Cheapest reasoning option. Good value for STEM.' },
    ],
  },
  anthropic: {
    label: 'Anthropic (Claude)',
    models: [
      { id: 'claude-opus-4-7',            tier: 'premium',   speed: 'medium', cost: '$5/$25 per 1M',   detail: '200K / 1M', note: 'Newest Claude. Best for nuanced + long-context work. 1M-context variant available.' },
      { id: 'claude-sonnet-4-6',          tier: 'premium',   speed: 'fast',   cost: '$3/$15 per 1M',   detail: '200K', note: 'Strong balance. Good default Claude.' },
      { id: 'claude-haiku-4-5-20251001',  tier: 'value',     speed: 'fastest', cost: '$1/$5 per 1M',   detail: '200K', note: 'Cheapest + fastest Claude. Extraction / classification.' },
    ],
  },
  groq: {
    label: 'Groq',
    models: [
      { id: 'llama-3.3-70b-versatile', tier: 'value', speed: 'fastest', cost: '$0.59/$0.79 per 1M', detail: '128K', note: 'Open-weights Llama on Groq LPUs. Sub-second responses.' },
      { id: 'llama-3.1-8b-instant',    tier: 'value', speed: 'fastest', cost: '$0.05/$0.08 per 1M', detail: '128K', note: 'Dirt cheap. Good for simple classification at scale.' },
    ],
  },
  gemini: {
    label: 'Google Gemini',
    models: [
      { id: 'gemini-2.5-pro',        tier: 'premium', speed: 'medium', cost: '$1.25/$10 per 1M',  detail: '2M',  note: 'Massive context. Long-prompt tier: $2.50/$15 per 1M above 200K tokens.' },
      { id: 'gemini-2.5-flash',      tier: 'value',   speed: 'fast',   cost: '$0.30/$2.50 per 1M', detail: '1M', note: 'Everyday pick for RAG / chat. Audio input $1/1M.' },
      { id: 'gemini-2.5-flash-lite', tier: 'value',   speed: 'fastest', cost: '$0.10/$0.40 per 1M', detail: '1M', note: 'Cheapest Gemini. Simple classification.' },
    ],
  },
  mistral: {
    label: 'Mistral',
    models: [
      { id: 'mistral-large-latest', tier: 'premium', speed: 'medium', cost: '$0.50/$1.50 per 1M', detail: '128K', note: 'Top-tier Mistral. Major price drop in 2026. Use for reasoning / multilingual.' },
      { id: 'mistral-small-latest', tier: 'value',   speed: 'fast',   cost: '$0.10/$0.30 per 1M', detail: '128K', note: 'Strong value. Default for most Mistral tasks.' },
      { id: 'codestral-latest',     tier: 'premium', speed: 'fast',   cost: '$0.30/$0.90 per 1M', detail: '256K', note: 'Tuned for code generation / autocomplete.' },
    ],
  },
  azure_openai: {
    label: 'Azure OpenAI',
    models: [
      { id: '(uses OpenAI model IDs)', tier: 'premium', speed: 'medium', cost: 'Your Azure pricing', detail: 'Model-dependent', note: 'Deployment-based. Use your deployment name, not the model ID, in Deployment Name.' },
    ],
  },
  ollama: {
    label: 'Ollama (local)',
    models: [
      { id: 'llama3.2',  tier: 'value',  speed: 'hw-dependent', cost: 'Free (local compute)', detail: '128K', note: 'Runs on your machine. No network. Size depends on params (1B/3B).' },
      { id: 'llama3.1',  tier: 'value',  speed: 'hw-dependent', cost: 'Free',                  detail: '128K', note: '8B / 70B / 405B. Pick what your GPU fits.' },
      { id: 'phi4',      tier: 'value',  speed: 'hw-dependent', cost: 'Free',                  detail: '16K',  note: '14B Microsoft. Strong for its size.' },
      { id: 'qwen2.5',   tier: 'value',  speed: 'hw-dependent', cost: 'Free',                  detail: '128K', note: 'Multilingual. Various sizes.' },
    ],
  },
};

// ─── IMAGE GENERATION ───
const PROVIDERS_IMAGE = {
  openai: {
    label: 'OpenAI (DALL-E)',
    models: [
      { id: 'dall-e-3', tier: 'premium', speed: 'medium', cost: '$0.04–0.12 / image', detail: 'up to 1792×1024', note: 'HD quality adds 2x cost. Best prompt adherence.' },
      { id: 'dall-e-2', tier: 'value',   speed: 'fast',   cost: '$0.016–0.02 / image', detail: '256–1024 sq',     note: 'Older, cheaper. Use dall-e-3 for new work.' },
    ],
  },
  stability: {
    label: 'Stability AI',
    models: [
      { id: 'stable-diffusion-xl-1024-v1-0', tier: 'value', speed: 'fast', cost: '~$0.03 / image', detail: '1024×1024', note: 'Open-source SDXL. Good cost/quality ratio.' },
      { id: 'stable-image-core',             tier: 'value', speed: 'fast', cost: '~$0.03 / image', detail: 'flexible',  note: 'Stability hosted API. Supports negative prompts.' },
      { id: 'stable-image-ultra',            tier: 'premium', speed: 'medium', cost: '~$0.08 / image', detail: 'flexible', note: 'Photoreal. Use for portraits / marketing.' },
    ],
  },
  replicate: {
    label: 'Replicate (Flux)',
    models: [
      { id: 'black-forest-labs/flux-1.1-pro',     tier: 'premium', speed: 'medium', cost: '~$0.04 / image', detail: 'flexible', note: 'Current Flux flagship. Strong prompt following.' },
      { id: 'black-forest-labs/flux-schnell',     tier: 'value',   speed: 'fastest', cost: '~$0.003 / image', detail: 'flexible', note: 'Cheapest Flux. 4-step distilled. Great at scale.' },
      { id: 'black-forest-labs/flux-dev',         tier: 'value',   speed: 'fast',   cost: '~$0.025 / image', detail: 'flexible', note: 'Mid-tier quality/cost.' },
    ],
  },
};

// ─── SPEECH-TO-TEXT ───
const PROVIDERS_STT = {
  openai: {
    label: 'OpenAI (Whisper)',
    models: [
      { id: 'whisper-1',               tier: 'value',   speed: 'fast',   cost: '$0.006 / min', detail: '99 langs', note: 'Widely used. Returns verbose_json with word timestamps.' },
      { id: 'gpt-4o-transcribe',       tier: 'premium', speed: 'medium', cost: '$0.006 / min', detail: '99 langs', note: 'Newer. Better for noisy audio / accents.' },
      { id: 'gpt-4o-mini-transcribe',  tier: 'value',   speed: 'fast',   cost: '$0.003 / min', detail: '99 langs', note: 'Cheapest OpenAI STT. Solid quality.' },
    ],
  },
  azure: {
    label: 'Azure Speech',
    models: [
      { id: '(Azure Speech default)', tier: 'value', speed: 'fast', cost: '~$1 / hour', detail: '100+ langs', note: 'Enterprise-grade. Supports custom acoustic models.' },
    ],
  },
  google: {
    label: 'Google STT',
    models: [
      { id: '(Google Cloud default)', tier: 'value', speed: 'fast', cost: '$0.016 / min (std) or $0.024 (enhanced)', detail: '125+ langs', note: 'Enhanced models for phone/video recommended.' },
    ],
  },
};

// ─── TEXT-TO-SPEECH ───
const PROVIDERS_TTS = {
  openai: {
    label: 'OpenAI TTS',
    models: [
      { id: 'tts-1',    tier: 'value',   speed: 'fast',   cost: '$15 / 1M chars', detail: '6 voices', note: 'Standard. Natural intonation. Good default.' },
      { id: 'tts-1-hd', tier: 'premium', speed: 'medium', cost: '$30 / 1M chars', detail: '6 voices', note: 'Higher quality. Use for customer-facing audio.' },
    ],
  },
  azure: {
    label: 'Azure Speech',
    models: [
      { id: '(Neural voice default)', tier: 'value', speed: 'fast', cost: '~$16 / 1M chars', detail: '400+ voices / 140+ langs', note: 'Huge voice library. SSML rate/pitch control.' },
    ],
  },
  elevenlabs: {
    label: 'ElevenLabs',
    models: [
      { id: 'eleven_multilingual_v2', tier: 'premium', speed: 'medium',  cost: '$100 / 1M chars', detail: '29 langs', note: 'Most natural. Use for narration / podcasts.' },
      { id: 'eleven_flash_v2',        tier: 'value',   speed: 'fastest', cost: '$50 / 1M chars',  detail: '32 langs', note: 'Current recommended fast model. ~75ms latency.' },
      { id: 'eleven_turbo_v2',        tier: 'value',   speed: 'fastest', cost: '$50 / 1M chars',  detail: '32 langs', note: 'Older low-latency option. ElevenLabs now recommends Flash.' },
    ],
  },
};

const CATEGORY_CONFIG = {
  llm:   { providers: PROVIDERS_LLM,   detailLabel: 'Context window',  footerPricing: 'Prices show input/output per 1M tokens.' },
  image: { providers: PROVIDERS_IMAGE, detailLabel: 'Output size',     footerPricing: 'Prices are per generated image (provider list prices).' },
  stt:   { providers: PROVIDERS_STT,   detailLabel: 'Languages',       footerPricing: 'Prices shown per minute of audio (or per hour where noted).' },
  tts:   { providers: PROVIDERS_TTS,   detailLabel: 'Voices / langs',  footerPricing: 'Prices shown per 1M characters (or plan-based for ElevenLabs).' },
};

const TIER_COLORS = {
  premium:   { bg: 'rgba(186, 111, 255, 0.15)', fg: '#ba6fff' },
  value:     { bg: 'rgba(78, 201, 176, 0.15)',  fg: '#4ec9b0' },
  reasoning: { bg: 'rgba(255, 180, 90, 0.15)',  fg: '#ffb45a' },
};

const SPEED_ICONS = {
  fastest:         '⚡⚡',
  fast:            '⚡',
  medium:          '∙',
  slow:            '🐢',
  'hw-dependent':  '⚙️',
};

export default function ModelCheatSheet({ category = 'llm', providerField = 'provider', modelField = 'model', formData = {} }) {
  const [open, setOpen] = useState(false);
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.llm;
  const currentProvider = formData[providerField];
  const currentModel = formData[modelField];

  const providersToShow = currentProvider && config.providers[currentProvider]
    ? { [currentProvider]: config.providers[currentProvider] }
    : config.providers;

  return (
    <div style={{ marginBottom: '15px', border: '1px solid var(--border-color)', borderRadius: '4px', background: 'var(--bg-tertiary)' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: '8px 10px', background: 'transparent', border: 'none',
          color: 'var(--text-secondary)', cursor: 'pointer', textAlign: 'left', fontSize: '11px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <span style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
          {open ? '▼' : '▶'} Model cheat sheet
          {currentProvider && ` — ${config.providers[currentProvider]?.label || currentProvider}`}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
          cost / speed / capability tradeoffs
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 10px 10px' }}>
          {Object.entries(providersToShow).map(([provId, prov]) => (
            <div key={provId} style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                {prov.label}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {prov.models.map(m => {
                  const tierC = TIER_COLORS[m.tier] || { bg: 'transparent', fg: 'var(--text-secondary)' };
                  const isCurrent = currentProvider === provId && currentModel === m.id;
                  return (
                    <div
                      key={m.id}
                      style={{
                        display: 'flex', flexDirection: 'column', gap: '2px',
                        padding: '5px 6px',
                        fontSize: '10.5px', lineHeight: '1.3',
                        background: isCurrent ? 'rgba(130, 170, 255, 0.12)' : 'transparent',
                        border: isCurrent ? '1px solid var(--accent-primary)' : '1px solid transparent',
                        borderRadius: '3px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <code style={{ fontFamily: 'monospace', color: 'var(--text-primary)', fontSize: '10.5px', flex: '0 1 auto', minWidth: 0, wordBreak: 'break-all' }}>{m.id}</code>
                        <span style={{ fontSize: '12px' }} title={m.speed}>{SPEED_ICONS[m.speed] || m.speed}</span>
                        <span style={{
                          fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.03em',
                          padding: '1px 5px', borderRadius: '3px',
                          background: tierC.bg, color: tierC.fg, fontWeight: 600,
                          flexShrink: 0,
                        }}>
                          {m.tier}
                        </span>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '10px', marginLeft: 'auto' }} title={config.detailLabel}>{m.detail}</span>
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '10px', lineHeight: '1.4' }}>
                        <span style={{ color: 'var(--text-tertiary)' }}>{m.cost}</span>
                        <span style={{ color: 'var(--text-tertiary)', margin: '0 5px' }}>·</span>
                        {m.note}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div style={{ marginTop: '10px', fontSize: '9.5px', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
            {config.footerPricing} Last reviewed 2026-04-22. Double-check with the provider before scaling.
            {' '}
            <span style={{ color: TIER_COLORS.premium.fg }}>premium</span> = best quality,
            {' '}
            <span style={{ color: TIER_COLORS.value.fg }}>value</span> = good cost/perf ratio,
            {' '}
            <span style={{ color: TIER_COLORS.reasoning.fg }}>reasoning</span> = chain-of-thought specialist.
          </div>
        </div>
      )}
    </div>
  );
}
